import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LogIn, UserPlus } from "lucide-react";
import { ASSETS, NAV_ITEMS, SITE } from "../lib/site";
import { trackCta } from "../lib/analytics";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong" : "bg-transparent"
      }`}
    >
      <div className="shell flex items-center justify-between h-16 lg:h-20">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center group"
          aria-label="Sloto69 Beranda"
        >
          <img
            src={ASSETS.logo}
            alt="Sloto69"
            width="176"
            height="58"
            className="h-10 w-auto max-w-[150px] object-contain transition duration-300 group-hover:brightness-110 lg:h-12 lg:max-w-[176px]"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-1" aria-label="Navigasi utama">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.to.replace(/\//g, "") || "beranda"}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-md transition-all whitespace-nowrap ${
                  isActive
                    ? "text-white bg-white/5"
                    : "text-slate-300 hover:text-white hover:bg-white/[0.04]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <a
            href={SITE.loginUrl}
            data-testid="cta-masuk-navbar"
            onClick={() => trackCta("click_login", "Login", "navbar")}
            className="btn-secondary !py-2 !px-4 text-sm"
          >
            <LogIn className="w-4 h-4" /> Masuk
          </a>
          <a
            href={SITE.registerUrl}
            data-testid="cta-daftar-navbar"
            onClick={() => trackCta("click_daftar", "Daftar", "navbar")}
            className="btn-primary !py-2 !px-4 text-sm"
          >
            <UserPlus className="w-4 h-4" /> Daftar
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Buka menu"
          aria-expanded={open}
          onClick={() => setOpen((s) => !s)}
          className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-white/10 text-white"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="xl:hidden glass-strong border-t border-white/5"
        >
          <div className="shell py-4 grid gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "beranda"}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-base ${
                    isActive
                      ? "text-white bg-white/5"
                      : "text-slate-300 hover:text-white hover:bg-white/[0.04]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-3">
              <a
                href={SITE.loginUrl}
                data-testid="cta-masuk-mobile"
                onClick={() => trackCta("click_login", "Login", "mobile_menu")}
                className="btn-secondary justify-center"
              >
                <LogIn className="w-4 h-4" /> Masuk
              </a>
              <a
                href={SITE.registerUrl}
                data-testid="cta-daftar-mobile"
                onClick={() => trackCta("click_daftar", "Daftar", "mobile_menu")}
                className="btn-primary justify-center"
              >
                <UserPlus className="w-4 h-4" /> Daftar
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
